// Links URLs an agent pastes or types into the composer, once they are finished.

import type { EditorState, MarkType, Transaction } from '@kimcchq/prosemirror-schema';
import { trimUrlPunctuation } from './link-bare-urls';

/** A URL in a block's plain text; U+FFFC stands in for an inline image or break and ends it. */
const DOC_URL = /\bhttps?:\/\/[^\s<>()[\]"￼]+/g;

/**
 * The library's input rule links a typed URL only when a space follows it, and a paste is never
 * linked, so a URL pasted at the end of a line stays plain. This links every unlinked URL in the
 * doc, except one that still ends at the cursor while typing: linking it half-typed would split
 * the link from the characters that follow.
 */
export function linkTypedUrls(state: EditorState, linkMark: MarkType, isPaste: boolean): Transaction | null {
  const tr = state.tr;
  let linked = false;

  state.doc.descendants((node, pos) => {
    if (!node.isTextblock) return true;
    const text = node.textBetween(0, node.content.size, undefined, '￼');
    for (const match of text.matchAll(DOC_URL)) {
      const url = trimUrlPunctuation(match[0]);
      const from = pos + 1 + (match.index ?? 0);
      const to = from + url.length;
      const stillTyping = !isPaste && state.selection.empty && to === state.selection.head;
      if (stillTyping || state.doc.rangeHasMark(from, to, linkMark)) continue;
      tr.addMark(from, to, linkMark.create({ href: url }));
      linked = true;
    }
    return false;
  });

  return linked ? tr : null;
}
